import React, { useEffect, useState } from "react"
import { graphql } from "gatsby"
import { getImage } from "gatsby-plugin-image"
import { Link, useTranslation } from "@zestia/gatsby-plugin-react-i18next"

import MainLayout from "@/layouts/MainLayout"
import SharedHead from "@/components/SharedHead"
import TwoColumnHero from "@/components/TwoColumnHero"
import G2ReviewsRating from "@/components/G2ReviewsRating"
import G2Badges from "@/components/G2Badges"
import G2ReviewsCarousel from "@/components/G2ReviewsCarousel"
import G2ReviewWall from "@/components/G2ReviewWall"
import FeatureDemoTabsHeader, {
  FeatureDemoTab,
} from "@/components/FeatureDemoTabsHeader"
import FeatureDemoTabsFooter from "@/components/FeatureDemoTabsFooter"
import FeatureDemoNoTabs from "@/components/FeatureDemoNoTabs"
import IntegrationsShowcase from "@/components/IntegrationsShowcase"
import ImageWithTextBlockContainer from "@/components/ImageWithTextBlockContainer"
import ImageWithTextBlockLinked from "@/components/ImageWithTextBlockLinked"
import ImageWithTextBlock from "@/components/ImageWithTextBlock"
import PricingSnapshot from "@/components/PricingSnapshot"
import ExploreSlider from "@/components/ExploreSlider"
import FAQ from "@/components/FAQ"
import CustomerStorySnapshot from "@/components/CustomerStorySnapshot"
import SanityContentWrapper from "@/components/SanityContentWrapper"
import StackingContentBlockContainer from "@/components/StackingContentBlockContainer"
import GatedContentForm from "@/components/GatedContentForm"
import KeyStats from "@/components/KeyStats"
import TypeformQuizContainer from "@/components/TypeformQuizContainer"
import CallToActionBanner from "@/components/CallToActionBanner"
import OAuthSignupButton from "@/components/OAuthSignupButton"
import FeatureHeroWithTestimonial from "@/components/FeatureHeroWithTestimonial"
import WebinarSignupBanner from "@/components/WebinarSignupBanner"
import IconTextCarousel from "@/components/IconTextCarousel"
import defaultIntegrationCarouselItems from "@/components/IconTextCarousel/defaultItems"
import FileDownloadBanner from "@/components/FileDownloadBanner"
import Divider from "@/components/Divider"
import getSanityFileSource from "@/utils/get-sanity-file-source"
import isExternalLink from "@/utils/is-external-link"

import findImage from "@/utils/find-image"
import createAvailableSlugsFromSanityTranslationMetadata from "@/utils/create-available-slugs-from-sanity-translation-metadata"
import getSanityGatsbyImage from "@/utils/get-sanity-gatsby-image"
import getSanityVideoSource from "@/utils/get-sanity-video-source"
import urlFor from "@/utils/sanity-url-builder"
import { setOfferCookie } from "@/utils/cookie-detection"
import {
  createLucideIcon,
  getExploreSliderIcon,
} from "@/utils/industry-page-icons"

const SECTIONS_WITH_OWN_TOP_DIVIDER = new Set([
  "integrationsShowcase",
  "featureHeroWithTestimonial",
  "landingPagePricingSection",
])

const NO_DIVIDER_AFTER = new Set(["accordion"])

const FULL_WIDTH_DIVIDER_ABOVE = new Set([
  "ctaBanner",
  "fileDownloadBanner",
  "imageTextBlockSection",
  "imageTextBlockTwoColumn",
])

const dividerWidthFor = (type, prevType) => {
  if (!prevType) return null
  if (NO_DIVIDER_AFTER.has(prevType)) return null
  if (type === "accordion") return null
  if (FULL_WIDTH_DIVIDER_ABOVE.has(type)) return "full"
  if (SECTIONS_WITH_OWN_TOP_DIVIDER.has(type)) return null
  return "1240"
}

const renderPortableText = value =>
  value ? <SanityContentWrapper>{value}</SanityContentWrapper> : null

const gatsbyImageFromSanity = (image, maxWidth = 800) => {
  if (!image?.asset) return null
  if (image.asset.gatsbyImageData) {
    return getImage(image.asset.gatsbyImageData)
  }
  return getImage(getSanityGatsbyImage(image, { maxWidth }))
}

const SanityImageLogo = ({ url, className, width, height, alt }) => (
  <img
    src={url}
    className={className}
    width={width}
    height={height}
    alt={alt || ""}
  />
)

const resolveButton = (button, tryFreeText) => {
  if (!button) {
    return { link: "/signup/", text: tryFreeText }
  }

  const text = button.buttonText || tryFreeText

  if (button.buttonType === "external") {
    return { link: button.link?.trim(), text }
  }

  const internalSlug = button.internalSlug || button.slug?.current
  if (button.buttonType === "internal" && internalSlug) {
    return { link: `/${internalSlug.replace(/^\/|\/$/g, "")}/`, text }
  }

  return { link: "/signup/", text }
}

export const Head = props => {
  const { seo, hidden } = props.data.sanityLandingPage

  return (
    <SharedHead
      {...props}
      title={seo?.seoTitle}
      description={seo?.seoDescription}
      noIndex={hidden}
    />
  )
}

const FlexibleLandingPageLayout = ({ location, data, pageContext }) => {
  const { t } = useTranslation()
  const page = data.sanityLandingPage
  const content = (page._rawContent || []).filter(
    item => item._type !== "dashedDivider",
  )
  const allFile = data.allFile

  const availableSlugs = createAvailableSlugsFromSanityTranslationMetadata(
    data.allSanityTranslationMetadata,
    pageContext,
  )

  const navOptions = {
    showLogIn: page.showNavLogin ?? false,
    showSignUp: page.showNavSignup ?? false,
    showNavBarKicker: page.showNavBarKicker ?? false,
    showLogo: page.showLogo ?? true,
  }

  const pageCategoryName = page.pageCategory?.name?.toLowerCase() ?? ""
  const pageTypeValue = page.pageType ?? ""
  const isPromotionalPage =
    pageCategoryName.includes("partner") ||
    pageCategoryName.includes("campaign") ||
    pageTypeValue === "partnerOffer" ||
    pageTypeValue === "campaign"

  const pricingSection = content.find(
    item => item._type === "landingPagePricingSection",
  )
  const isMonthlyBilling = pricingSection?.billingFrequency || false
  const pricingSectionId = pricingSection
    ? `pricing-section-${pricingSection._key}`
    : undefined

  const [tier, setTier] = useState()
  const [frequency, setFrequency] = useState(
    isMonthlyBilling ? "MONTHLY" : "ANNUAL",
  )

  useEffect(() => {
    content.forEach(item => {
      if (item.discountCode) {
        setOfferCookie(item.discountCode)
      }
    })
  }, [content, tier])

  const createFormId = (urlSegment, formTitle) => {
    const combined = `${urlSegment} ${formTitle}`
    return combined
      .replace(/\s+/g, "")
      .replace(/[^a-zA-Z0-9]/g, "")
      .replace(/^./, str => str.toLowerCase())
  }

  return (
    <MainLayout
      showNavLinks={!isPromotionalPage}
      {...navOptions}
      hideFooterLinks={isPromotionalPage}
      availableSlugs={availableSlugs || []}
    >
      {content.map((item, index) => {
        const key = item._key || item._id
        const dividerWidth = dividerWidthFor(
          item._type,
          content[index - 1]?._type,
        )

        const rendered = (() => {
        switch (item._type) {
          case "landingHero": {
            const heroImage = gatsbyImageFromSanity(item.image, 900)
            const logoUrl = item.logo?.asset
              ? urlFor(item.logo).height(96).url()
              : null
            const videoUrl = item.video?.asset
              ? getSanityVideoSource(item.video)
              : null
            const g2 = item.includeG2SocialProof ? <G2ReviewsRating /> : null

            if (item.button?.buttonType === "scrollToPricing") {
              return (
                <div key={key} className="mt-12">
                  <TwoColumnHero
                    kicker={item.kickerText}
                    title={item.title}
                    description={renderPortableText(item.text)}
                    scrollToPricingSection={{
                      id: pricingSectionId,
                      text: item.button.buttonText || t("common.try-free"),
                    }}
                    logo={
                      logoUrl
                        ? { src: logoUrl, alt: item.logo?.alt || item.title }
                        : undefined
                    }
                    image={
                      heroImage
                        ? {
                            src: heroImage,
                            alt: item.image?.alt || item.title,
                          }
                        : undefined
                    }
                    video={videoUrl ? { file: videoUrl } : undefined}
                  >
                    {g2}
                  </TwoColumnHero>
                </div>
              )
            }

            if (item.button?.buttonType === "signup") {
              return (
                <div key={key} className="mt-12">
                  <TwoColumnHero
                    kicker={item.kickerText}
                    title={item.title}
                    description={renderPortableText(item.text)}
                    logo={
                      logoUrl
                        ? { src: logoUrl, alt: item.logo?.alt || item.title }
                        : undefined
                    }
                    image={
                      heroImage
                        ? {
                            src: heroImage,
                            alt: item.image?.alt || item.title,
                          }
                        : undefined
                    }
                    video={videoUrl ? { file: videoUrl } : undefined}
                  >
                    <OAuthSignupButton
                      integrationType={item.button.integrationType}
                      buttonText={
                        item.button.buttonText || t("common.try-free")
                      }
                      location={location}
                      className="btn"
                    />
                    {g2}
                  </TwoColumnHero>
                </div>
              )
            }

            return (
              <div key={key} className="mt-12">
                <TwoColumnHero
                  kicker={item.kickerText}
                  title={item.title}
                  description={renderPortableText(item.text)}
                  button={resolveButton(item.button, t("common.try-free"))}
                  logo={
                    logoUrl
                      ? { src: logoUrl, alt: item.logo?.alt || item.title }
                      : undefined
                  }
                  image={
                    heroImage
                      ? {
                          src: heroImage,
                          alt: item.image?.alt || item.title,
                        }
                      : undefined
                  }
                  video={videoUrl ? { file: videoUrl } : undefined}
                >
                  {g2}
                </TwoColumnHero>
              </div>
            )
          }

          case "landingPagePricingSection":
            return (
              <div key={key} id={`pricing-section-${item._key}`}>
                <PricingSnapshot
                  title={item.title}
                  description={item.description}
                  discount={item.discount}
                  showAllColumns={item.showAllColumns}
                  frequency={frequency}
                  onSelectPlan={setTier}
                  onTogglePlanFrequency={setFrequency}
                  showFrequencyToggle={!isMonthlyBilling}
                  tier={tier}
                  location={location}
                  closeModal={() => setTier(null)}
                  showFreeAsColumn={!item.hideFreePlan}
                  planButtonText={item.planButtonText}
                />
              </div>
            )

          case "g2Badges":
            return (
              <div key={key} className="container-fluid row center-xs">
                <div className="col-md-6">
                  <G2Badges />
                </div>
              </div>
            )

          case "g2ReviewsCarousel":
            return (
              <div key={key}>
                <G2ReviewsCarousel
                  reviews={t("home.g2-review-quotes", { returnObjects: true })}
                />
              </div>
            )

          case "g2ReviewWall":
            return (
              <div key={key}>
                <G2ReviewWall />
              </div>
            )

          case "keyStats":
            return (
              <div key={key}>
                <KeyStats stats={item.stats} />
              </div>
            )

          case "inlineStackedCards":
            return (
              <div key={key}>
                <StackingContentBlockContainer
                  title={item.title}
                  subheading={item.sectionText}
                  blocks={(item.cards || []).map(card => ({
                    kicker: card.kickerText,
                    title: card.heading,
                    description: card.content,
                    image: card.image
                      ? {
                          src: gatsbyImageFromSanity(card.image, 900),
                          alt: card.image?.alt || card.heading || "",
                        }
                      : null,
                    ...(card.buttonLink?.trim()
                      ? {
                          link: {
                            url: card.buttonLink,
                            text: card.buttonText,
                          },
                        }
                      : {}),
                    playlistId: card.playlistId,
                  }))}
                />
              </div>
            )

          case "accordion":
            return (
              <div key={key} className="bg-white pt-20 pb-0">
                <FAQ
                  className="mb-0"
                  title={item.title}
                  data={(item.content || []).map(faqItem => ({
                    title: faqItem.title,
                    description: renderPortableText(faqItem.content),
                    _rawContent: faqItem.content,
                  }))}
                />
              </div>
            )

          case "gatedContent": {
            const segments = location.pathname
              .split("/")
              .filter(segment => segment.length > 0)
            const urlSegment = segments[segments.length - 1] || "default"
            const formId = createFormId(urlSegment, item.formTitle)

            return (
              <div key={key}>
                <GatedContentForm
                  title={item.title}
                  text={item.description}
                  centered="true"
                  checklistItems={item.bulletPoints}
                  formUrl={item.formContent}
                  formTitle={item.formTitle}
                  formId={formId}
                />
              </div>
            )
          }

          case "typeformQuiz":
            return (
              <div key={key}>
                <TypeformQuizContainer
                  title={item.title}
                  description={item.description}
                  selectedQuizId={item.selectedQuiz}
                  popup={false}
                />
              </div>
            )

          case "ctaBanner":
            return (
              <div key={key} className="mb-0">
                <CallToActionBanner
                  title={item.title}
                  description={item.text}
                  action={props => {
                    if (item.button?.buttonType === "signup") {
                      return (
                        <OAuthSignupButton
                          {...props}
                          integrationType={item.button?.integrationType}
                          buttonText={item.button?.buttonText}
                          location={location}
                        />
                      )
                    }

                    if (item.button?.buttonType === "scrollToPricing") {
                      return (
                        <button
                          {...props}
                          className="btn btn--primary"
                          onClick={() => {
                            const pricingEl =
                              document.getElementById(pricingSectionId)
                            pricingEl?.scrollIntoView({ behavior: "smooth" })
                          }}
                        >
                          {item.button?.buttonText}
                        </button>
                      )
                    }

                    const ctaLink =
                      item.button?.link?.trim() ||
                      (item.button?.internalSlug
                        ? `/${item.button.internalSlug}/`
                        : item.button?.slug?.current
                          ? `/${item.button.slug.current}/`
                          : "/signup/")

                    if (isExternalLink(ctaLink)) {
                      return (
                        <a
                          {...props}
                          href={ctaLink}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.button?.buttonText}
                        </a>
                      )
                    }

                    return (
                      <Link {...props} to={ctaLink}>
                        {item.button?.buttonText}
                      </Link>
                    )
                  }}
                />
              </div>
            )

          case "fileDownloadBanner": {
            const fileDownloadUrl = item.file?.asset?.url
              ? item.file.asset.url
              : item.file
                ? getSanityFileSource(item.file, item.title)
                : null

            return (
              <div key={key}>
                <FileDownloadBanner
                  type="plainBanner"
                  wrapper
                  className="mb-0"
                  title={item.title}
                  kickerText={item.kickerText}
                  buttonText={item.buttonText || "Download"}
                  fileDownloadUrl={fileDownloadUrl}
                />
              </div>
            )
          }

          case "productTourGrey":
          case "productTourWhite":
            return (
              <WebinarSignupBanner
                key={key}
                type={item._type === "productTourWhite" ? "threeDates" : "default"}
                kickerText={item.kickerText || undefined}
                title={item.title || undefined}
                description={item.description || undefined}
                buttonText={item.buttonText || undefined}
                buttonLink={item.buttonLink || undefined}
              />
            )

          case "integrationsShowcase":
            return (
              <div key={key}>
                <IntegrationsShowcase
                  title={item.title}
                  description={item.description}
                  topDividerWidth="full"
                  className="mb-0"
                />
              </div>
            )

          case "integrationCarousel":
            return (
              <div key={key} className="container-fluid">
                <IconTextCarousel
                  title={
                    item.title || t("home.integration-carousel.title")
                  }
                  items={defaultIntegrationCarouselItems}
                />
              </div>
            )

          case "featureDemoTabs":
            return (
              <FeatureDemoTabsFooter
                key={key}
                title={item.title}
                description={renderPortableText(item.description)}
                highlights={
                  item.tabs?.map(tab => ({
                    title: tab.title,
                    description: renderPortableText(tab.description),
                    image: gatsbyImageFromSanity(tab.image, 900),
                    imageAlt: tab.image?.alt || tab.title,
                  })) || []
                }
              />
            )

          case "featureDemoTabsTop":
            return (
              <div key={key}>
                <FeatureDemoTabsHeader>
                  {(item.tabs || []).map(tab => {
                    const tabImage = gatsbyImageFromSanity(tab.image, 900)
                    return (
                      <FeatureDemoTab
                        key={tab._key}
                        name={tab.tabLabel || tab.title}
                        kicker={tab.kickerText}
                        title={tab.title}
                        description={renderPortableText(tab.description)}
                        image={
                          tabImage
                            ? {
                                src: tabImage,
                                alt: tab.image?.alt || tab.title,
                              }
                            : undefined
                        }
                        imageAlt={tab.image?.alt || tab.title}
                      />
                    )
                  })}
                </FeatureDemoTabsHeader>
              </div>
            )

          case "featureDemo":
          case "featureDemoKeyBenefits":
            return (
              <FeatureDemoNoTabs
                key={key}
                kicker={item.kickerText}
                title={item.title}
                description={renderPortableText(item.description)}
                image={gatsbyImageFromSanity(item.image, 900)}
                imageAlt={item.image?.alt || item.title}
                highlights={
                  item.highlights?.map(highlight => ({
                    title: highlight.title,
                    description: highlight.description,
                    icon: highlight.icon
                      ? createLucideIcon(highlight.icon)
                      : undefined,
                  })) || []
                }
              />
            )

          case "featureHeroWithTestimonial": {
            const productImage = gatsbyImageFromSanity(item.productImage, 1200)
            const testimonialImage = gatsbyImageFromSanity(
              item.testimonialImage,
              800,
            )
            const logoUrl = item.logo?.asset ? urlFor(item.logo).url() : null

            return (
              <FeatureHeroWithTestimonial
                key={key}
                kicker={item.kickerText}
                title={item.title}
                description={item.description}
                buttonText={item.buttonText}
                buttonLink={item.buttonLink}
                productImage={
                  productImage
                    ? {
                        src: productImage,
                        alt: item.productImage?.alt || item.title,
                      }
                    : undefined
                }
                testimonial={{
                  quote: item.quote,
                  name: item.name,
                  role: item.role,
                  image: testimonialImage
                    ? {
                        src: testimonialImage,
                        alt: item.testimonialImage?.alt || item.name,
                      }
                    : undefined,
                  logo: logoUrl
                    ? props => <SanityImageLogo {...props} url={logoUrl} />
                    : undefined,
                  logoAlt: item.logo?.alt || item.name,
                  logoWidth: item.logoWidth || 160,
                  logoHeight: item.logoHeight || 40,
                }}
              />
            )
          }

          case "imageTextBlockSection":
          case "imageTextBlockTwoColumn": {
            const cardCount = item.blocks?.length || 1
            const columns =
              cardCount === 1
                ? 1
                : item.columns === 2 || item._type === "imageTextBlockTwoColumn"
                  ? 2
                  : Math.min(3, cardCount)

            return (
              <ImageWithTextBlockContainer
                key={key}
                columnLayout={columns}
                title={item.title}
                text={item.sectionText}
                ctaButton={
                  item.showCtaButton ?? item._type !== "imageTextBlockTwoColumn"
                }
                className="mb-0"
              >
                {(item.blocks || []).map(block => {
                  const blockImage = gatsbyImageFromSanity(block.image, 600)
                  return (
                    <ImageWithTextBlock
                      key={block._key}
                      title={block.title}
                      text={block.text}
                      className={
                        item.plainBackground
                          ? "image-text-block--bg-none"
                          : undefined
                      }
                      image={
                        blockImage
                          ? {
                              src: blockImage,
                              alt: block.image?.alt || block.title,
                            }
                          : undefined
                      }
                    />
                  )
                })}
              </ImageWithTextBlockContainer>
            )
          }

          case "linkedImageTextBlockSection":
            return (
              <ImageWithTextBlockContainer
                key={key}
                className="mb-0"
                columnLayout={item.blocks?.length || 3}
                title={item.title}
                text={item.sectionText}
              >
                {(item.blocks || []).map(block => (
                  <ImageWithTextBlockLinked
                    key={block._key}
                    kicker={block.kickerText || t("common.blog")}
                    title={block.title}
                    text={block.text}
                    link={block.link}
                    image={
                      block.backgroundImage && allFile
                        ? {
                            src: getImage(
                              findImage(allFile, block.backgroundImage),
                            ),
                            alt: block.imageText,
                          }
                        : null
                    }
                    imageText={block.imageText}
                  />
                ))}
              </ImageWithTextBlockContainer>
            )

          case "exploreSlider":
            return (
              <ExploreSlider
                key={key}
                title={item.title}
                description={item.description}
                slides={
                  item.slides?.map(slide => ({
                    title: slide.title,
                    description: slide.description,
                    link: slide.link,
                    icon: getExploreSliderIcon(slide.icon),
                    explore: slide.exploreText,
                  })) || []
                }
              />
            )

          case "customerStorySnapshot": {
            const customerStoryLink =
              item.link ||
              (item.customerStory?.slug?.current
                ? `/resources/customer-stories/${item.customerStory.slug.current}/`
                : null)
            const logoUrl = item.logo?.asset
              ? urlFor(item.logo).url()
              : null

            if (!item.name || !customerStoryLink) {
              return null
            }

            return (
              <CustomerStorySnapshot
                key={key}
                alt={item.alt || item.name}
                logo={
                  logoUrl
                    ? props => <SanityImageLogo {...props} url={logoUrl} />
                    : undefined
                }
                logoHeight={item.logoHeight || 32}
                logoWidth={item.logoWidth || 32}
                name={item.name}
                title={item.personTitle}
                quote={item.quote}
                text={item.text}
                link={customerStoryLink}
                backgroundImage={gatsbyImageFromSanity(item.backgroundImage, 1200)}
                image={gatsbyImageFromSanity(item.overlayImage, 600)}
                svg={
                  !item.overlayImage?.asset && logoUrl
                    ? props => <SanityImageLogo {...props} url={logoUrl} />
                    : undefined
                }
                imageHeight={item.overlayImageHeight || 300}
                imageWidth={item.overlayImageWidth || 300}
              />
            )
          }

          default:
            return null
        }
        })()

        if (!rendered) return null

        const precedesFaq = content[index + 1]?._type === "accordion"
        const followsFaq = content[index - 1]?._type === "accordion"
        const faqSpacingClass = [
          precedesFaq && "pb-20",
          followsFaq && "pt-20",
        ]
          .filter(Boolean)
          .join(" ")

        return (
          <React.Fragment key={key}>
            {dividerWidth ? <Divider width={dividerWidth} /> : null}
            {faqSpacingClass ? (
              <div className={faqSpacingClass}>{rendered}</div>
            ) : (
              rendered
            )}
          </React.Fragment>
        )
      })}
    </MainLayout>
  )
}

export const query = graphql`
  query ($language: String!, $id: String!) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }

    allSanityTranslationMetadata {
      nodes {
        translations {
          value {
            ... on SanityLandingPage {
              _id
              language
              slug {
                current
              }
              pageType
              pageCategory {
                name
              }
            }
          }
        }
      }
    }

    allFile(
      filter: {
        sourceInstanceName: { eq: "images" }
        relativeDirectory: { eq: "blog-bgs" }
      }
    ) {
      edges {
        node {
          name
          childImageSharp {
            gatsbyImageData
          }
        }
      }
    }

    sanityLandingPage(_id: { eq: $id }) {
      id
      pageType
      pageCategory {
        name
      }
      hidden
      showNavLogin
      showNavSignup
      showNavBarKicker
      showLogo
      seo {
        seoTitle
        seoDescription
      }
      _rawContent(resolveReferences: { maxDepth: 8 })
    }
  }
`

export default FlexibleLandingPageLayout
